import { format, isValid } from 'date-fns'

export type EntityDateFieldMode = 'date' | 'datetime'

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const parseDateOnlyValue = (value: string): Date | undefined => {
  const match = DATE_ONLY_PATTERN.exec(value)
  if (!match) return undefined

  const [, year, month, day] = match
  const parsed = new Date(Number(year), Number(month) - 1, Number(day))
  return isValid(parsed) ? parsed : undefined
}

export const parseEntityDateFieldValue = (
  value: string | null | undefined,
): Date | undefined => {
  const trimmed = value?.trim()
  if (!trimmed) return undefined

  const dateOnly = parseDateOnlyValue(trimmed)
  if (dateOnly) return dateOnly

  const parsed = new Date(trimmed)
  return isValid(parsed) ? parsed : undefined
}

export const serializeEntityDateFieldValue = (
  date: Date | undefined,
  mode: EntityDateFieldMode,
): string | null => {
  if (!date) return null
  return mode === 'date' ? format(date, 'yyyy-MM-dd') : date.toISOString()
}

export const formatEntityDateFieldValue = (
  value: string | null | undefined,
  mode: EntityDateFieldMode,
): string | null => {
  const date = parseEntityDateFieldValue(value)
  if (!date) return null
  return mode === 'date'
    ? format(date, 'MMM d, yyyy')
    : format(date, 'MMM d, yyyy h:mm a')
}

export const toEntityIsoDateTime = (
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim()
  if (!trimmed) return null

  // A bare YYYY-MM-DD has no time-of-day intent; serialize as UTC midnight so
  // the calendar date is preserved on the wire regardless of the user's
  // timezone. (Picker-sourced datetime strings already carry an explicit
  // offset and round-trip cleanly through .toISOString() below.)
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return `${trimmed}T00:00:00.000Z`
  }

  const date = parseEntityDateFieldValue(trimmed)
  return date ? date.toISOString() : null
}

export const toEntityDateOnly = (
  value: string | null | undefined,
): string | null => {
  const date = parseEntityDateFieldValue(value)
  return date ? format(date, 'yyyy-MM-dd') : null
}
